import { limparInstagram } from "./contatoLoja";
import { formatarCelular, celularDigitos } from "./perfilCliente";

// Espelha o cadastro do cliente na loja pública (`clientes_finais`) na lista
// de Clientes do Print Control (`clientes`).
//
// - Cliente novo no site: vira um cliente do CRM (origem "site").
// - Cliente que mudou os dados no site (Minha conta → Editar): o CRM recebe só
//   os campos que ELE alterou. Ajustes manuais do vendedor em campos que o
//   cliente não mexeu continuam valendo. Pra saber o que mudou, o CRM guarda em
//   `siteSnapshot` os valores do site da última sincronização.

// Campos espelhados: chave no site -> chave no CRM. `obrigatorio` = nunca
// sobrescreve com vazio (nome/celular são obrigatórios no site).
const CAMPOS = [
  { site: "nome", crm: "nome", obrigatorio: true, valor: s => s.nome },
  { site: "celular", crm: "celular", obrigatorio: true, valor: s => formatarCelular(s.celular) },
  { site: "dataNasc", crm: "aniversario", valor: s => aniversarioDe(s.dataNasc) },
  { site: "instagram", crm: "instagram", valor: s => s.instagram },
];

// AAAA-MM-DD -> DD/MM (o CRM guarda só dia/mês, formato usado nos avisos de aniversário).
export const aniversarioDe = dataNasc =>
  /^\d{4}-\d{2}-\d{2}$/.test(dataNasc || "") ? dataNasc.split("-").reverse().slice(0, 2).join("/") : "";

export function dadosDoSite(cadastro = {}) {
  return {
    nome: String(cadastro.nome || "").trim(),
    celular: celularDigitos(cadastro.celular),
    dataNasc: String(cadastro.dataNasc || ""),
    instagram: limparInstagram(cadastro.instagram),
    email: cadastro.email || "",
  };
}

const snapshotDe = site => Object.fromEntries(CAMPOS.map(({ site: chave }) => [chave, site[chave]]));

export function novoClienteDoSite(id, cadastro, agoraIso) {
  const site = dadosDoSite(cadastro);
  return {
    nome: site.nome,
    celular: formatarCelular(site.celular),
    email: site.email,
    aniversario: aniversarioDe(site.dataNasc),
    instagram: site.instagram,
    aceitaWhatsapp: false,
    tipo: "Novo",
    origem: "site",
    clienteFinalId: id,
    criadoEm: cadastro.data_criacao || agoraIso,
    siteSnapshot: snapshotDe(site),
  };
}

/** Campos do CRM a atualizar para um cliente já espelhado, ou null se nada mudou. */
export function atualizacaoDoSite(cliente, cadastro) {
  const site = dadosDoSite(cadastro);
  const atual = snapshotDe(site);
  const anterior = cliente.siteSnapshot;
  const patch = {};
  let mudou = false;

  for (const campo of CAMPOS) {
    const valor = campo.valor(site);
    if (!anterior) {
      // Espelhado antes deste recurso: não dá pra saber o que o vendedor mexeu,
      // então só completa o que está vazio no CRM.
      if (valor && !cliente[campo.crm]) patch[campo.crm] = valor;
    } else if (anterior[campo.site] !== atual[campo.site]) {
      mudou = true;
      if (valor || !campo.obrigatorio) patch[campo.crm] = valor;
    }
  }
  if (!anterior || mudou) patch.siteSnapshot = atual;
  return Object.keys(patch).length ? patch : null;
}

/**
 * Compara a lista do CRM com os cadastros do site.
 * Devolve { criar: [dados], atualizar: [{ id, patch }] } — quem chama grava.
 */
export function planejarEspelho(clientes, cadastros, agoraIso = new Date().toISOString()) {
  const espelhados = new Map(clientes.filter(c => c.clienteFinalId).map(c => [c.clienteFinalId, c]));
  const celulares = new Set(clientes.map(c => celularDigitos(c.celular)).filter(Boolean));
  const criar = [], atualizar = [];

  for (const { id, ...cadastro } of cadastros) {
    const cliente = espelhados.get(id);
    if (cliente) {
      const patch = atualizacaoDoSite(cliente, cadastro);
      if (patch) atualizar.push({ id: cliente.id, patch });
      continue;
    }
    const celular = celularDigitos(cadastro.celular);
    // Mesmo celular de um cliente que o vendedor já cadastrou: não duplica.
    if (celular && celulares.has(celular)) continue;
    criar.push(novoClienteDoSite(id, cadastro, agoraIso));
  }
  return { criar, atualizar };
}
